import * as React from "react";
import { useCallback, useMemo } from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  RowData,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Table as TanStackTable,
} from "@tanstack/react-table";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "./data-table-pagination";
import { FixedPageSizeDataTablePagination } from "./fixed-page-size-pagination";
import { DataTableToolbar } from "./data-table-toolbar";
import { FloatingAction } from "./floating-action";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface TableMeta<TData extends RowData> {}

  interface ColumnMeta<TData extends RowData, TValue> {
    className?: string;
  }
}

// Function to create a unique atom for each table instance
const createColumnVisibilityAtom = (tableId: string) => {
  return atomWithStorage<VisibilityState>(
    `dataTableColumnVisibility_${tableId}`,
    {},
  );
};

export interface FacetedFilterDef {
  columnId: string;
  title?: string;
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
}

interface DataTableProps<TData extends RowData> {
  /**
   * Unique identifier for this table instance
   */
  tableId: string;
  columns: ColumnDef<TData>[];
  data: TData[];
  searchColumn?: string;
  searchPlaceholder?: string;
  facetedFilters?: FacetedFilterDef[];
  className?: string;
  floatingActionItems?: (
    table: Readonly<TanStackTable<TData>>,
  ) => React.ReactNode[];
  columnVisibility?: VisibilityState;
  /**
   * Optional callback when column visibility changes
   */
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  /**
   * Default sort state for this table, e.g. [{ id: 'createdAt', desc: true }]
   */
  defaultSort?: SortingState;
  /**
   * Manual pagination flag - defaults to false, set to true when implementing server-side pagination
   */
  manualPagination?: boolean;
  /**
   * Current page for controlled pagination
   */
  currentPage?: number;
  /**
   * Page size for controlled pagination
   */
  pageSize?: number;
  /**
   * Total row count for controlled pagination (used for calculating total pages)
   */
  rowCount?: number;
  /**
   * Callback for pagination changes when using controlled pagination
   */
  onPaginationChange?: (
    updater:
      | { pageIndex: number; pageSize: number }
      | ((state: { pageIndex: number; pageSize: number }) => {
          pageIndex: number;
          pageSize: number;
        }),
  ) => void;
  /**
   * Callback for column filter changes
   */
  onColumnFiltersChange?: (filters: Record<string, unknown>) => void;
  /**
   * If true, will use a fixed page size pagination component instead of the standard one
   */
  useFixedPageSizePagination?: boolean;
}

export function DataTable<TData extends RowData>({
  tableId,
  columns,
  data,
  searchColumn = "",
  searchPlaceholder = "Filter...",
  facetedFilters,
  className,
  floatingActionItems,
  onColumnVisibilityChange,
  defaultSort,
  manualPagination,
  currentPage,
  pageSize,
  rowCount,
  onPaginationChange,
  onColumnFiltersChange,
  useFixedPageSizePagination = false,
}: Readonly<DataTableProps<TData>>) {
  // Create a memoized atom for this specific table instance
  const columnVisibilityAtom = useMemo(
    () => createColumnVisibilityAtom(tableId),
    [tableId],
  );

  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] = useAtom(columnVisibilityAtom);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  // Initialize sorting with defaultSort if provided
  const [sorting, setSorting] = React.useState<SortingState>(defaultSort ?? []);
  const [pagination, setPagination] = React.useState({
    pageIndex: currentPage !== undefined ? currentPage - 1 : 0,
    pageSize: pageSize || 10,
  });

  // Update internal pagination state when controlled props change
  React.useEffect(() => {
    if (currentPage !== undefined && pageSize !== undefined) {
      setPagination({
        pageIndex: currentPage - 1,
        pageSize,
      });
    }
  }, [currentPage, pageSize]);

  // Handle column visibility changes
  const handleColumnVisibilityChange = useCallback(
    (
      updaterOrValue:
        | VisibilityState
        | ((old: VisibilityState) => VisibilityState),
    ) => {
      const newValue =
        typeof updaterOrValue === "function"
          ? updaterOrValue(columnVisibility)
          : updaterOrValue;
      setColumnVisibility(newValue);
      onColumnVisibilityChange?.(newValue);
    },
    [setColumnVisibility, onColumnVisibilityChange, columnVisibility],
  );

  // Handle column filter changes and pass to parent if needed
  const handleColumnFiltersChange = useCallback(
    (
      updaterOrValue:
        | ColumnFiltersState
        | ((old: ColumnFiltersState) => ColumnFiltersState),
    ) => {
      // First handle the state update
      const newFilters =
        typeof updaterOrValue === "function"
          ? updaterOrValue(columnFilters)
          : updaterOrValue;

      setColumnFilters(newFilters);

      // Then notify the parent if callback exists
      if (onColumnFiltersChange) {
        // Convert to a Record for easier consumption by parent components
        const filtersRecord: Record<string, unknown> = {};
        newFilters.forEach((filter) => {
          filtersRecord[filter.id] = filter.value;
        });
        onColumnFiltersChange(filtersRecord);
      }
    },
    [onColumnFiltersChange, columnFilters],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      pagination,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onPaginationChange: onPaginationChange || setPagination,
    autoResetPageIndex: false,
    manualPagination: manualPagination,
    pageCount:
      manualPagination && rowCount
        ? Math.ceil(rowCount / (pageSize || 10))
        : undefined,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const selectedCount = Object.keys(rowSelection).length;
  const isSomeSelected = selectedCount > 0;

  return (
    <div className={className}>
      <DataTableToolbar
        table={table}
        searchColumn={searchColumn}
        searchPlaceholder={searchPlaceholder}
        facetedFilters={facetedFilters}
      />
      <div className="mt-4 rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className={header.column.columnDef.meta?.className ?? ""}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="group/row"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cell.column.columnDef.meta?.className ?? ""}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {useFixedPageSizePagination ? (
        <FixedPageSizeDataTablePagination
          table={table}
          fixedPageSize={pageSize || 10}
        />
      ) : (
        <DataTablePagination table={table} />
      )}

      <FloatingAction
        visible={isSomeSelected}
        items={floatingActionItems?.(table)}
      />
    </div>
  );
}
